import { useCallback, useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import turfDistance from '@turf/distance';
import { useMeasurement, formatDistance } from './useMeasurement';

// Self-contained map-side wiring for the click-to-measure tool. Wraps
// useMeasurement (which owns the geometry + distance math) and attaches the
// sources / layers / handlers to a Mapbox map, so a host component only needs a
// toggle. Modeled on the proven wiring in MapView.tsx, but isolated here so the
// Crown Pine PortfolioMap can adopt it without touching the shared main map.
//
// Interaction: click to drop waypoints, a rubber-band preview + live distance
// label follows the cursor, double-click or Esc finishes the run, Backspace
// undoes the last point.
//
// A second, mutually-exclusive AREA mode drags a lon/lat-aligned rectangle and
// reports its acreage. It shares the measure suppression guard with the
// distance tool (the host folds both into one `measureActiveRef`), so every
// existing hover handler stays quiet in either mode.

const EMPTY_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };
const MEASURE_COLOR = '#ef4444';        // matches designTokens.colors.measurement
const MEASURE_LABEL = '#D49880';        // matches designTokens.colors.measurementLabel
const MEASURE_LAYERS = ['measurement-lines', 'measurement-preview', 'measurement-points'];
const AREA_LAYERS = ['measurement-area-fill', 'measurement-area-line'];

// Spherical-earth rectangle area. For a lon/lat box λ1..λ2, φ1..φ2 the exact
// spherical area is R²·|Δλ|·|sin φ2 − sin φ1| — no projection, no turf/area
// dependency. ACRE_M2 matches the repo's constant (api-server/routes/briefing.js).
const EARTH_R_M = 6371008.8;
const ACRE_M2 = 4046.86;
const DEG = Math.PI / 180;

function rectAcres(a: [number, number], b: [number, number]): number {
  const m2 = EARTH_R_M * EARTH_R_M
    * Math.abs((b[0] - a[0]) * DEG)
    * Math.abs(Math.sin(b[1] * DEG) - Math.sin(a[1] * DEG));
  return m2 / ACRE_M2;
}

// ≥100 ac reads as a whole number with thousands separators; below that the
// decimal carries real information. A section (640 ac) and up also gets mi².
function formatArea(acres: number): string {
  const ac = acres >= 100 ? Math.round(acres).toLocaleString() : acres.toFixed(1);
  return acres >= 640 ? `${ac} ac · ${(acres / 640).toFixed(1)} mi²` : `${ac} ac`;
}

function rectFC(a: [number, number], b: [number, number]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: { acres: rectAcres(a, b) },
      geometry: {
        type: 'Polygon',
        coordinates: [[[a[0], a[1]], [b[0], a[1]], [b[0], b[1]], [a[0], b[1]], [a[0], a[1]]]],
      },
    }],
  };
}

interface Opts {
  mapRef: React.MutableRefObject<mapboxgl.Map | null>;
  ready: boolean;      // map style has loaded
  styleEpoch: number;  // bumped on basemap style swap → re-add sources/layers
  active: boolean;     // distance measure mode on
  enabled: boolean;    // gate ALL wiring — off for the frozen Integra map
  areaActive?: boolean;       // drag-rectangle acreage mode on
  onExitArea?: () => void;    // Esc with no rectangle drawn disarms the mode
}

export function useMapMeasurement({ mapRef, ready, styleEpoch, active, enabled, areaActive = false, onExitArea }: Opts) {
  const {
    measurementsRef, currentPointsRef, measurementCount,
    handleMapClick, finishMeasurement, undoLastPoint, clearAll,
    getLineGeoJSON, getPointGeoJSON, getPreviewGeoJSON,
  } = useMeasurement();

  const activeRef = useRef(active);
  activeRef.current = active;
  const segTotalRef = useRef(0);
  const endpointMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const cursorMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const attachedRef = useRef(false);

  // Area mode state. All refs — the rectangle is drawn straight into the map
  // source on every mousemove, so re-rendering the host on drag would be pure
  // cost. onExitAreaRef keeps the once-attached key handler on the latest callback.
  const areaActiveRef = useRef(areaActive);
  areaActiveRef.current = areaActive;
  const onExitAreaRef = useRef(onExitArea);
  onExitAreaRef.current = onExitArea;
  const areaAnchorRef = useRef<[number, number] | null>(null);
  const areaRectRef = useRef<[[number, number], [number, number]] | null>(null);
  const areaDraggingRef = useRef(false);
  const areaLabelRef = useRef<mapboxgl.Marker | null>(null);
  const areaAttachedRef = useRef(false);

  const label = useCallback((m: mapboxgl.Map, lngLat: [number, number], text: string) => {
    const el = document.createElement('div');
    el.style.cssText =
      `font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:11px;font-weight:500;` +
      `color:${MEASURE_LABEL};background:rgba(58,70,80,0.92);padding:2px 6px;border-radius:3px;` +
      `border:1px solid ${MEASURE_COLOR};pointer-events:none;white-space:nowrap;`;
    el.textContent = text;
    return new mapboxgl.Marker({ element: el, anchor: 'left', offset: [8, 0] }).setLngLat(lngLat).addTo(m);
  }, []);

  const updateSources = useCallback((m: mapboxgl.Map) => {
    (m.getSource('measurement-lines') as mapboxgl.GeoJSONSource | undefined)?.setData(getLineGeoJSON());
    (m.getSource('measurement-points') as mapboxgl.GeoJSONSource | undefined)?.setData(getPointGeoJSON());
    endpointMarkersRef.current.forEach(k => k.remove());
    endpointMarkersRef.current = [];
    for (const meas of measurementsRef.current) {
      if (meas.points.length >= 2) {
        endpointMarkersRef.current.push(label(m, meas.points[meas.points.length - 1], formatDistance(meas.total)));
      }
    }
  }, [getLineGeoJSON, getPointGeoJSON, measurementsRef, label]);

  const addLayers = useCallback((m: mapboxgl.Map) => {
    for (const id of MEASURE_LAYERS) if (!m.getSource(id)) m.addSource(id, { type: 'geojson', data: EMPTY_FC });
    if (!m.getLayer('measurement-lines'))
      m.addLayer({ id: 'measurement-lines', type: 'line', source: 'measurement-lines', paint: { 'line-color': MEASURE_COLOR, 'line-width': 2 } });
    if (!m.getLayer('measurement-preview'))
      m.addLayer({ id: 'measurement-preview', type: 'line', source: 'measurement-preview', paint: { 'line-color': MEASURE_COLOR, 'line-width': 1.5, 'line-dasharray': [2, 4] } });
    if (!m.getLayer('measurement-points'))
      m.addLayer({ id: 'measurement-points', type: 'circle', source: 'measurement-points', paint: { 'circle-radius': 4, 'circle-color': '#fff', 'circle-stroke-color': MEASURE_COLOR, 'circle-stroke-width': 2 } });
    if (!m.getSource('measurement-area')) m.addSource('measurement-area', { type: 'geojson', data: EMPTY_FC });
    if (!m.getLayer('measurement-area-fill'))
      m.addLayer({ id: 'measurement-area-fill', type: 'fill', source: 'measurement-area', paint: { 'fill-color': MEASURE_COLOR, 'fill-opacity': 0.12 } });
    if (!m.getLayer('measurement-area-line'))
      m.addLayer({ id: 'measurement-area-line', type: 'line', source: 'measurement-area', paint: { 'line-color': MEASURE_COLOR, 'line-width': 1.5 } });
  }, []);

  // Paint the rectangle + its acreage chip. Called on every drag frame and
  // again on mouseup, so the frozen rectangle is simply the last frame drawn.
  const drawRect = useCallback((m: mapboxgl.Map, a: [number, number], b: [number, number]) => {
    (m.getSource('measurement-area') as mapboxgl.GeoJSONSource | undefined)?.setData(rectFC(a, b));
    const text = formatArea(rectAcres(a, b));
    if (!areaLabelRef.current) areaLabelRef.current = label(m, b, text);
    else { areaLabelRef.current.setLngLat(b); areaLabelRef.current.getElement().textContent = text; }
  }, [label]);

  const clearArea = useCallback((m: mapboxgl.Map) => {
    areaAnchorRef.current = null;
    areaRectRef.current = null;
    areaDraggingRef.current = false;
    (m.getSource('measurement-area') as mapboxgl.GeoJSONSource | undefined)?.setData(EMPTY_FC);
    areaLabelRef.current?.remove();
    areaLabelRef.current = null;
  }, []);

  const clearPreview = useCallback((m: mapboxgl.Map) => {
    (m.getSource('measurement-preview') as mapboxgl.GeoJSONSource | undefined)?.setData(EMPTY_FC);
    cursorMarkerRef.current?.remove();
    cursorMarkerRef.current = null;
  }, []);

  const clearAllMeasurements = useCallback(() => {
    clearAll();
    segTotalRef.current = 0;
    const m = mapRef.current;
    if (m) { clearPreview(m); updateSources(m); }
  }, [clearAll, mapRef, clearPreview, updateSources]);

  const finishActiveMeasurement = useCallback(() => {
    const m = mapRef.current;
    if (!m) return;
    finishMeasurement();
    segTotalRef.current = 0;
    updateSources(m);
    clearPreview(m);
  }, [clearPreview, finishMeasurement, mapRef, updateSources]);

  // Attach map-level handlers once. They gate on activeRef, so they're inert
  // until measure mode is on, and survive basemap style swaps (map-level, not
  // layer-scoped).
  useEffect(() => {
    const m = mapRef.current;
    if (!enabled || !ready || !m || attachedRef.current) return;
    attachedRef.current = true;

    m.on('click', (e) => {
      if (!activeRef.current) return;
      const pts = currentPointsRef.current;
      if (pts.length > 0) segTotalRef.current += turfDistance(pts[pts.length - 1], [e.lngLat.lng, e.lngLat.lat], { units: 'miles' });
      handleMapClick(e.lngLat);
      updateSources(m);
    });
    m.on('dblclick', (e) => {
      if (!activeRef.current) return;
      e.preventDefault();
      finishMeasurement();
      segTotalRef.current = 0;
      updateSources(m);
      clearPreview(m);
    });
    m.on('mousemove', (e) => {
      if (!activeRef.current || currentPointsRef.current.length === 0) return;
      const cursor: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      (m.getSource('measurement-preview') as mapboxgl.GeoJSONSource | undefined)?.setData(getPreviewGeoJSON(cursor));
      const pts = currentPointsRef.current;
      const total = segTotalRef.current + turfDistance(pts[pts.length - 1], cursor, { units: 'miles' });
      if (!cursorMarkerRef.current) cursorMarkerRef.current = label(m, cursor, formatDistance(total));
      else { cursorMarkerRef.current.setLngLat(cursor); cursorMarkerRef.current.getElement().textContent = formatDistance(total); }
    });
  }, [enabled, ready, mapRef, currentPointsRef, handleMapClick, finishMeasurement, getPreviewGeoJSON, updateSources, clearPreview, label]);

  // Area-mode drag handlers, attached once like the distance ones above. They
  // gate on areaActiveRef, so they're inert unless the rectangle mode is armed.
  useEffect(() => {
    const m = mapRef.current;
    if (!enabled || !ready || !m || areaAttachedRef.current) return;
    areaAttachedRef.current = true;

    m.on('mousedown', (e) => {
      if (!areaActiveRef.current || e.originalEvent.button !== 0) return;
      clearArea(m);                       // a new drag replaces the frozen rect
      areaAnchorRef.current = [e.lngLat.lng, e.lngLat.lat];
      areaDraggingRef.current = true;
    });
    m.on('mousemove', (e) => {
      const anchor = areaAnchorRef.current;
      if (!areaActiveRef.current || !areaDraggingRef.current || !anchor) return;
      const cur: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      areaRectRef.current = [anchor, cur];
      drawRect(m, anchor, cur);
    });
    m.on('mouseup', (e) => {
      const anchor = areaAnchorRef.current;
      if (!areaActiveRef.current || !areaDraggingRef.current || !anchor) return;
      areaDraggingRef.current = false;
      const cur: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      // A click without a drag leaves nothing behind rather than a zero-area sliver.
      if (cur[0] === anchor[0] || cur[1] === anchor[1]) { clearArea(m); return; }
      areaRectRef.current = [anchor, cur];
      drawRect(m, anchor, cur);
    });
  }, [enabled, ready, mapRef, clearArea, drawRect]);

  // (Re)add sources + layers on first ready and after every basemap style swap.
  // A style swap wipes the sources, so the frozen rectangle is repainted too.
  useEffect(() => {
    const m = mapRef.current;
    if (!enabled || !ready || !m) return;
    addLayers(m);
    updateSources(m);
    const r = areaRectRef.current;
    if (r) drawRect(m, r[0], r[1]);
  }, [enabled, ready, styleEpoch, mapRef, addLayers, updateSources, drawRect]);

  // Mode toggle: crosshair cursor, disable dbl-click zoom, keep the measure
  // layers on top while active; tear down the preview when leaving the mode.
  useEffect(() => {
    const m = mapRef.current;
    if (!enabled || !ready || !m) return;
    if (active) {
      m.getCanvas().style.cursor = 'crosshair';
      m.doubleClickZoom.disable();
      for (const id of MEASURE_LAYERS) if (m.getLayer(id)) m.moveLayer(id);
    } else {
      m.getCanvas().style.cursor = '';
      m.doubleClickZoom.enable();
      clearPreview(m);
    }
  }, [enabled, active, ready, mapRef, clearPreview]);

  // Area mode toggle: crosshair, drag-to-pan off (the drag draws the rectangle
  // instead), area layers on top. Disarming re-enables pan and clears the rect.
  useEffect(() => {
    const m = mapRef.current;
    if (!enabled || !ready || !m) return;
    if (areaActive) {
      m.getCanvas().style.cursor = 'crosshair';
      m.dragPan.disable();
      for (const id of AREA_LAYERS) if (m.getLayer(id)) m.moveLayer(id);
      // Releasing off-canvas never reaches the map's own mouseup, which would
      // leave the rectangle glued to the cursor. Freeze it here instead.
      const onUp = () => { areaDraggingRef.current = false; };
      window.addEventListener('mouseup', onUp);
      return () => window.removeEventListener('mouseup', onUp);
    } else {
      m.dragPan.enable();
      // Don't stomp the distance tool's crosshair when the two modes swap.
      if (!activeRef.current) m.getCanvas().style.cursor = '';
      clearArea(m);
    }
  }, [enabled, areaActive, ready, mapRef, clearArea]);

  // Unmount safety net: a mid-drag unmount must never leave dragPan disabled
  // or a floating acreage chip in the DOM.
  useEffect(() => () => {
    areaLabelRef.current?.remove();
    areaLabelRef.current = null;
    try { mapRef.current?.dragPan.enable(); } catch { /* map already removed */ }
  }, [mapRef]);

  // Keyboard shortcuts while measuring (either mode).
  useEffect(() => {
    if (!enabled || (!active && !areaActive)) return;
    const onKey = (ev: KeyboardEvent) => {
      const m = mapRef.current;
      if (!m) return;
      if (areaActiveRef.current) {
        // Esc clears the current rectangle first; a second Esc (nothing drawn)
        // disarms the mode. Backspace has no meaning here.
        if (ev.key !== 'Escape') return;
        if (areaRectRef.current || areaAnchorRef.current) clearArea(m);
        else onExitAreaRef.current?.();
        return;
      }
      if (ev.key === 'Escape') {
        finishMeasurement();
        segTotalRef.current = 0;
        updateSources(m);
        clearPreview(m);
      } else if (ev.key === 'Backspace') {
        ev.preventDefault();
        undoLastPoint();
        updateSources(m);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [enabled, active, areaActive, mapRef, finishMeasurement, undoLastPoint, updateSources, clearPreview, clearArea]);

  return { count: measurementCount, clearAll: clearAllMeasurements, finish: finishActiveMeasurement };
}
